import React, { Fragment, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import { fetchAllPartners, deactivatePartner } from "../../services/PartnerService";
import {
    resendPartnerSuperAdminVerification,
    fixPartnerSuperAdminEmail,
} from "../../services/UserService";
import { ROLE_SLUGS } from "../../utils/auth/permissionConstants";
import { scrollToPageTop } from "../../utils/scrollToPageTop";
import RegisterPartner from "./RegisterPartner";
import EditPartnerModal from "./Modals/EditPartnerModal";
import useGuidedTour from "../../hooks/useGuidedTour";
import {
    MANAGE_PARTNERS_OVERVIEW_TOUR_ID,
    buildManagePartnersOverviewTourSteps,
} from "./ManagePartners.tour";
import { SECURITY_OUTRO_WITHOUT_PIN_TOUR_ID } from "../../hooks/faqOutroSteps";
import { showMutationError } from "../../utils/showMutationError";

const statusLabel = (status) => status.charAt(0).toUpperCase() + status.slice(1);

const partnerStatusBadgeClass = (status) => {
    if (status === 'active')   return 'badge-success';
    if (status === 'pending')  return 'badge-warning';
    if (status === 'archived') return 'badge-light';
    return 'badge-secondary';
};

const PartnerStatusBadge = ({ status }) => (
    <span className={`badge badge-rounded ${partnerStatusBadgeClass(status)}`}>
        {statusLabel(status)}
    </span>
);

const superAdminStatusBadgeClass = (status) =>
    status === 'active' ? 'badge-success' : 'badge-secondary';

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
    if (lastPage <= 1) return null;
    return (
        <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page <= 1 || isLoading}
                onClick={onPrev}
                aria-label="Previous page"
            >
                <i className="fa fa-angle-left me-1" />
                Prev
            </button>
            <span className="text-muted small">Page {page} of {lastPage}</span>
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page >= lastPage || isLoading}
                onClick={onNext}
                aria-label="Next page"
            >
                Next
                <i className="fa fa-angle-right ms-1" />
            </button>
        </div>
    );
};

const ManagePartners = () => {
  const queryClient = useQueryClient();

  const authUser = useSelector(state => state.auth.authUser);
  const userRole = authUser?.role_slug;

  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [partnerToDeactivate, setPartnerToDeactivate] = useState(null);
  const [partnerToEdit, setPartnerToEdit] = useState(null);
  const [superAdminPartner, setSuperAdminPartner] = useState(null);
  const [showFixEmailForm, setShowFixEmailForm] = useState(false);
  const [fixEmailInput, setFixEmailInput] = useState('');
  const [fixEmailFieldErrors, setFixEmailFieldErrors] = useState({});
  const [page, setPage] = useState(1);

  const closeSuperAdminModal = () => {
      setSuperAdminPartner(null);
      setShowFixEmailForm(false);
      setFixEmailInput('');
      setFixEmailFieldErrors({});
  };

  const completedTourIds = authUser?.completed_tour_ids ?? [];
  const managePartnersOverviewTourSteps = useMemo(
      () => buildManagePartnersOverviewTourSteps({ completedTourIds }),
      [completedTourIds]
  );
  useGuidedTour(
      MANAGE_PARTNERS_OVERVIEW_TOUR_ID,
      managePartnersOverviewTourSteps,
      {
          enabled: !!authUser?.id && userRole === ROLE_SLUGS.EXEC_ADMIN,
          alsoMarkCompleted: [SECURITY_OUTRO_WITHOUT_PIN_TOUR_ID],
      }
  );

  const { data, isPending, isError, isFetching } = useQuery({
    queryKey: ['partners', page],
    queryFn: () => fetchAllPartners({ page }),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const partners = data?.data?.data ?? [];
  const totalPages = data?.data?.meta?.last_page ?? 1;

  const deactivateMutation = useMutation({
    mutationFn: (partnerId) => deactivatePartner(partnerId),
    onSuccess: async () => {
      toast.success("Partner deactivated successfully.");
      await queryClient.invalidateQueries({ queryKey: ['partners'] });
      setPage(1);
      setPartnerToDeactivate(null);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to deactivate partner.");
    },
  });

  const resendSuperAdminMutation = useMutation({
    mutationFn: (partnerId) => resendPartnerSuperAdminVerification(partnerId),
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'A new verification pin is on the way.');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Could not resend the verification email.');
    },
  });

  const fixSuperAdminEmailMutation = useMutation({
    mutationFn: ({ partnerId, newEmail }) => fixPartnerSuperAdminEmail(partnerId, newEmail),
    onSuccess: async (response) => {
      toast.success(response?.data?.message || 'Email corrected.');
      await queryClient.invalidateQueries({ queryKey: ['partners'] });
      closeSuperAdminModal();
    },
    onError: (err) => showMutationError(err, {
      fallback: 'Could not correct the email.',
      onFieldErrors: setFixEmailFieldErrors,
    }),
  });

  const renderActionButtons = (partner) => (
      <div className="d-flex gap-2 flex-wrap">
          <button
              className="btn btn-sm btn-outline-info"
              onClick={() => setSuperAdminPartner(partner)}
              aria-label={`Super admin for ${partner.name}`}
              data-tour="manage-partners-super-admin-button"
          >
              <i className="fa fa-user me-1" aria-hidden="true" />
              Super Admin
          </button>
          {userRole === ROLE_SLUGS.EXEC_ADMIN && (
              <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPartnerToEdit(partner)}
                  aria-label={`Edit ${partner.name}`}
                  data-tour="manage-partners-edit-button"
              >
                  <i className="fa fa-pen me-1" aria-hidden="true" />
                  Edit
              </button>
          )}
          {userRole === ROLE_SLUGS.EXEC_ADMIN && partner.status === 'active' && (
              <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setPartnerToDeactivate(partner)}
                  aria-label={`Deactivate ${partner.name}`}
                  data-tour="manage-partners-deactivate-button"
              >
                  <i className="fa fa-ban me-1" aria-hidden="true" />
                  Deactivate
              </button>
          )}
      </div>
  );

  return (
    <Fragment>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2">
              <h4 className="card-title mb-0">Partners</h4>
              <button
                type="button"
                onClick={() => setShowRegisterModal(true)}
                className="btn btn-sm btn-primary ms-auto d-none d-lg-inline-flex align-items-center"
                data-tour="manage-partners-add-button"
              >
                <i className="fa fa-plus me-1" aria-hidden="true" />
                Add Partner
              </button>
              <button
                type="button"
                onClick={() => setShowRegisterModal(true)}
                className="btn btn-primary rounded-circle ms-auto d-inline-flex d-lg-none align-items-center justify-content-center shadow-sm"
                style={{ width: '2.5rem', height: '2.5rem', padding: 0 }}
                title="Add partner"
                aria-label="Add partner"
                data-tour="manage-partners-add-button"
              >
                <i className="fas fa-plus" aria-hidden="true" />
              </button>
            </div>
            <div className="card-body p-0">
              {isPending ? (
                <div className="d-flex justify-content-center py-4">
                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                </div>
              ) : isError ? (
                <p className="text-center text-danger py-4 mb-0">
                    Failed to load partners. Please try again.
                </p>
              ) : partners.length === 0 ? (
                <div className="text-center py-4" data-tour="manage-partners-list">
                    <p className="text-muted mb-2">No partners have been added yet.</p>
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={() => setShowRegisterModal(true)}
                        data-tour="manage-partners-add-button"
                    >
                        <i className="fa fa-plus me-1" aria-hidden="true" />
                        Add Partner
                    </button>
                </div>
              ) : (
                <div data-tour="manage-partners-list">
                    <div className="table-responsive d-none d-xl-block px-3 pt-2">
                        <table className="table verticle-middle mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">Name</th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Date Added</th>
                                    <th scope="col" className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {partners.map((partner) => (
                                    <tr key={partner.id}>
                                        <td className="fw-semibold" style={{ overflowWrap: 'anywhere' }}>{partner.name}</td>
                                        <td><PartnerStatusBadge status={partner.status} /></td>
                                        <td className="text-nowrap">{partner.created_when}</td>
                                        <td className="text-end">{renderActionButtons(partner)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                        <table className="table verticle-middle mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">Partner</th>
                                    <th scope="col">Status</th>
                                    <th scope="col" className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {partners.map((partner) => (
                                    <tr key={partner.id}>
                                        <td>
                                            <div className="fw-semibold" style={{ overflowWrap: 'anywhere' }}>{partner.name}</div>
                                            <div className="small text-muted mt-1">
                                                <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                {partner.created_when}
                                            </div>
                                        </td>
                                        <td><PartnerStatusBadge status={partner.status} /></td>
                                        <td className="text-end">{renderActionButtons(partner)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <ul className="list-group list-group-flush d-block d-md-none">
                        {partners.map((partner) => (
                            <li key={partner.id} className="list-group-item py-3">
                                <div className="d-flex align-items-center gap-3 mb-1 flex-wrap">
                                    <span className="fw-semibold text-body" style={{ overflowWrap: 'anywhere' }}>{partner.name}</span>
                                    <PartnerStatusBadge status={partner.status} />
                                </div>
                                <div className="small text-muted">
                                    <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                    {partner.created_when}
                                </div>
                                <div className="mt-2">{renderActionButtons(partner)}</div>
                            </li>
                        ))}
                    </ul>
                </div>
              )}
              <PaginationBar
                page={page}
                lastPage={totalPages}
                isLoading={isFetching}
                onPrev={() => { setPage(p => Math.max(p - 1, 1)); scrollToPageTop(); }}
                onNext={() => { setPage(p => Math.min(p + 1, totalPages)); scrollToPageTop(); }}
              />
            </div>
          </div>
        </div>
      </div>

      <RegisterPartner
        show={showRegisterModal}
        onHide={() => setShowRegisterModal(false)}
      />

      <EditPartnerModal
        show={!!partnerToEdit}
        onHide={() => setPartnerToEdit(null)}
        partner={partnerToEdit}
      />

      <Modal show={!!superAdminPartner} onHide={closeSuperAdminModal} centered>
        <Modal.Header closeButton>
            <Modal.Title>Super Admin: {superAdminPartner?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {superAdminPartner?.super_admin ? (
                <div className="d-flex flex-column gap-3">
                    <div className="row g-2">
                        <div className="col-sm-4 fw-semibold">Name</div>
                        <div className="col-sm-8">
                            {superAdminPartner.super_admin.full_name}
                            {superAdminPartner.super_admin.preferred_name && (
                                <span className="text-muted ms-1">
                                    ({superAdminPartner.super_admin.preferred_name})
                                </span>
                            )}
                            {superAdminPartner.super_admin.deleted && (
                                <span className="badge bg-danger ms-2">Deleted</span>
                            )}
                        </div>
                    </div>
                    <div className="row g-2">
                        <div className="col-sm-4 fw-semibold">Email</div>
                        <div className="col-sm-8" style={{ overflowWrap: 'anywhere' }}>
                            {superAdminPartner.super_admin.email}
                        </div>
                    </div>
                    <div className="row g-2">
                        <div className="col-sm-4 fw-semibold">Phone</div>
                        <div className="col-sm-8">
                            {superAdminPartner.super_admin.phone || <span className="text-muted">Not set</span>}
                        </div>
                    </div>
                    <div className="row g-2">
                        <div className="col-sm-4 fw-semibold">Status</div>
                        <div className="col-sm-8">
                            <span className={`badge badge-rounded ${superAdminStatusBadgeClass(superAdminPartner.super_admin.status)}`}>
                                {statusLabel(superAdminPartner.super_admin.status)}
                            </span>
                        </div>
                    </div>
                    {superAdminPartner.super_admin.deleted && (
                        <p className="text-muted small mb-0">
                            This super admin was removed. To reactivate the seat, register a new super admin for this partner.
                        </p>
                    )}
                </div>
            ) : (
                <p className="text-muted mb-0">No super admin on record for this partner.</p>
            )}
        </Modal.Body>
        <Modal.Footer>
            {superAdminPartner?.super_admin?.status === 'pending' && !superAdminPartner.super_admin.deleted && (
                showFixEmailForm ? (
                    <form
                        className="d-flex gap-2 w-100 align-items-start flex-wrap"
                        noValidate
                        onSubmit={(e) => {
                            e.preventDefault();
                            setFixEmailFieldErrors({});
                            fixSuperAdminEmailMutation.mutate({
                                partnerId: superAdminPartner.id,
                                newEmail: fixEmailInput.trim(),
                            });
                        }}
                    >
                        <div className="flex-grow-1" style={{ minWidth: '200px' }}>
                            <input
                                type="email"
                                className={`form-control${fixEmailFieldErrors.email ? ' is-invalid' : ''}`}
                                placeholder="New email address"
                                value={fixEmailInput}
                                onChange={(e) => {
                                    setFixEmailInput(e.target.value);
                                    if (fixEmailFieldErrors.email) {
                                        setFixEmailFieldErrors((prev) => {
                                            const next = { ...prev };
                                            delete next.email;
                                            return next;
                                        });
                                    }
                                }}
                                autoFocus
                                disabled={fixSuperAdminEmailMutation.isPending}
                            />
                            {fixEmailFieldErrors.email && (
                                <div className="invalid-feedback d-block">{fixEmailFieldErrors.email}</div>
                            )}
                        </div>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!fixEmailInput.trim() || fixSuperAdminEmailMutation.isPending}
                        >
                            {fixSuperAdminEmailMutation.isPending && <i className="fa fa-spinner fa-spin me-1" />}
                            Send
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => { setShowFixEmailForm(false); setFixEmailInput(''); }}
                            disabled={fixSuperAdminEmailMutation.isPending}
                        >
                            Cancel
                        </button>
                    </form>
                ) : (
                    <div className="d-flex gap-2 flex-wrap me-auto">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => resendSuperAdminMutation.mutate(superAdminPartner.id)}
                            disabled={resendSuperAdminMutation.isPending}
                        >
                            {resendSuperAdminMutation.isPending && <i className="fa fa-spinner fa-spin me-1" />}
                            Resend verification email
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowFixEmailForm(true)}
                        >
                            Fix email
                        </button>
                    </div>
                )
            )}
            {!showFixEmailForm && (
                <button className="btn btn-secondary" onClick={closeSuperAdminModal}>
                    Close
                </button>
            )}
        </Modal.Footer>
      </Modal>

      <Modal show={!!partnerToDeactivate} onHide={() => setPartnerToDeactivate(null)} centered>
        <Modal.Header closeButton>
            <Modal.Title>Deactivate Partner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <p className="mb-0">
                Are you sure you want to deactivate <strong>{partnerToDeactivate?.name}</strong>?
                Their users will no longer be able to access the platform.
            </p>
        </Modal.Body>
        <Modal.Footer>
            <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setPartnerToDeactivate(null)}
                disabled={deactivateMutation.isPending}
            >
                Cancel
            </button>
            <button
                type="button"
                className="btn btn-danger"
                onClick={() => deactivateMutation.mutate(partnerToDeactivate.id)}
                disabled={deactivateMutation.isPending}
            >
                {deactivateMutation.isPending && <i className="fa fa-spinner fa-spin me-1" />}
                Deactivate
            </button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default ManagePartners;
